import React, { useState, useRef, useLayoutEffect, useEffect, useCallback } from 'react';
import './ResponsivePreviewPanel.css';
import IconDesktop from '@/assets/icons/icon-desktop.svg?react';
import IconTablet from '@/assets/icons/icon-tablet.svg?react';
import IconMobile from '@/assets/icons/icon-mobile.svg?react';
import IconDesktopFilled from '@/assets/icons/icon-desktop-filled.svg?react';
import IconTabletFilled from '@/assets/icons/icon-tablet-filled.svg?react';
import IconMobileFilled from '@/assets/icons/icon-mobile-filled.svg?react';
import IconRecenter from '@/assets/icons/icon-recenter.svg?react';
import IconRecenterFocus from '@/assets/icons/icon-recenter-focus.svg?react';
import { SelectItem } from '@/components/ui/select';
import { Tooltip } from '@/components/tooltips/Tooltip/Tooltip';
import clsx from 'clsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setIsPanningMode, setPanZoom } from '@/store/zoom/panZoomSlice';
import type { Breakpoint } from '@builder4/shared/types/layout';
import { selectPageWidth, selectPageHeight, selectBreakpoint } from '@/store/system/selectors';
import { selectTranslateX, selectTranslateY } from '@/store/zoom/selectors';
import { LightDarkMode } from '@/components/widgets/LightDarkMode/LightDarkMode';
import { zoomPageToFitScreen, getNewFinalTranslation, MIN_ZOOM } from '@/utils/zoomHelper';
import { ROOT_NODE, type NodeId, useEditor } from '@craftjs/core';
import { ZoomSelect } from './ZoomSelect';
import useBreakpoint from '@/hooks/useBreakpoint';
import { useCanvasFocus } from '@/context/CanvasFocusContext';
import { getTranslateToScrollIntoView } from '@/utils/canvasScrollIntoView';
import { useNodeDragAutoScroll } from '@/hooks/useNodeDragAutoScroll';

interface ResponsivePreviewPanelProps {
  className?: string;
  frameRef: HTMLDivElement | null;
  loading?: boolean;
}
import { EVENT, PROPERTY } from '@/analytics/mixpanel';
import { useMixpanel } from '@/hooks/useMixpanel';

const breakpoints: Breakpoint[] = ['desktop', 'tablet', 'mobile'];

const icons = {
  desktop: {
    active: IconDesktopFilled,
    inactive: IconDesktop,
  },
  tablet: {
    active: IconTabletFilled,
    inactive: IconTablet,
  },
  mobile: {
    active: IconMobileFilled,
    inactive: IconMobile,
  },
};

export const ResponsivePreviewPanel: React.FC<ResponsivePreviewPanelProps> = ({
  className,
  frameRef,
  loading,
}) => {
  const dispatch = useAppDispatch();
  const storeBreakpoint = useAppSelector(selectBreakpoint);
  const pageWidth = useAppSelector(selectPageWidth);
  const pageHeight = useAppSelector(selectPageHeight);
  const [selectedSize, setSelectedSize] = useState<Breakpoint>(storeBreakpoint);
  const [highlightStyle, setHighlightStyle] = useState({ width: 0, left: 0 });
  const [isAnimated, setIsAnimated] = useState(false);
  const { switchBreakpoint } = useBreakpoint();
  const track = useMixpanel();
  const { registerFocusCanvas } = useCanvasFocus();

  const {
    dom: pageDom,
    query,
    selectedNodeId,
  } = useEditor((state, query) => {
    const selectedIterator = state.events.selected.values();
    const firstSelected = selectedIterator.next().value as NodeId | undefined;
    return {
      dom: query.node(ROOT_NODE).get()?.dom,
      selectedNodeId: firstSelected,
    };
  });

  const reduxTranslateX = useAppSelector(selectTranslateX);
  const reduxTranslateY = useAppSelector(selectTranslateY);

  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(reduxTranslateX);
  const [translateY, setTranslateY] = useState(reduxTranslateY);
  const [panningModeEnabled, setPanningModeEnabled] = useState(false);
  const [isCanvasActive, setIsCanvasActive] = useState(false);
  const [isZoomPanelActive, setIsZoomPanelActive] = useState(false);
  useNodeDragAutoScroll({ frameRef, setTranslateX, setTranslateY });

  // For panning
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const originX = useRef(0);
  const originY = useRef(0);
  const scaleRef = useRef(scale);
  const translateXRef = useRef(translateX);
  const translateYRef = useRef(translateY);

  const wheelTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  useEffect(() => {
    translateXRef.current = translateX;
  }, [translateX]);

  useEffect(() => {
    translateYRef.current = translateY;
  }, [translateY]);

  const getSelectedElementCenter = useCallback(() => {
    if (!selectedNodeId || selectedNodeId === ROOT_NODE) return null;
    if (!pageDom) return null;
    const selectedNode = query.node(selectedNodeId).get();
    const selectedDom = selectedNode?.dom as HTMLElement | undefined;
    if (!selectedDom) return null;
    const pageRect = pageDom.getBoundingClientRect();
    const selectedRect = selectedDom.getBoundingClientRect();
    const currentScale = scaleRef.current;
    return {
      x: (selectedRect.left - pageRect.left + selectedRect.width / 2) / currentScale,
      y: (selectedRect.top - pageRect.top + selectedRect.height / 2) / currentScale,
    };
  }, [selectedNodeId, pageDom, query]);

  const getTranslateForFocus = useCallback(
    (
      focusPoint: { x: number; y: number },
      targetPoint: { x: number; y: number },
      nextScale: number,
      containerRect: DOMRect,
      pageDimensions: { width: number; height: number },
    ) => {
      let newTranslateX = targetPoint.x - focusPoint.x * nextScale;
      let newTranslateY = targetPoint.y - focusPoint.y * nextScale;
      const scaledWidth = pageDimensions.width * nextScale;
      const scaledHeight = pageDimensions.height * nextScale;

      if (scaledWidth <= containerRect.width) {
        newTranslateX = (containerRect.width - scaledWidth) / 2;
      }
      if (scaledHeight <= containerRect.height) {
        newTranslateY = (containerRect.height - scaledHeight) / 2;
      }

      return { translateX: newTranslateX, translateY: newTranslateY };
    },
    [],
  );

  const clampScale = useCallback((value: number) => Math.min(5, Math.max(MIN_ZOOM, value)), []);

  const getUnscaledPageHeight = useCallback(() => {
    if (pageDom) {
      return pageDom.getBoundingClientRect().height / scaleRef.current;
    }
    return pageHeight;
  }, [pageDom, pageHeight]);

  const applyZoomScale = useCallback(
    (
      nextScale: number,
      options?: {
        focusSelected?: boolean;
        anchorPoint?: { x: number; y: number };
      },
    ) => {
      // Fullscreen uses 100% viewport width — zoom is not applicable
      if (pageWidth === undefined) return;

      const canvasContainer =
        frameRef || (document.querySelector('.page-container') as HTMLElement | null);
      if (!canvasContainer) return;

      const rect = canvasContainer.getBoundingClientRect();
      const containerWidth = rect.width;
      const containerHeight = rect.height;
      const clampedScale = clampScale(nextScale);
      const pHeight = getUnscaledPageHeight();
      const selectedCenter = options?.focusSelected ? getSelectedElementCenter() : null;

      if (selectedCenter) {
        const focused = getTranslateForFocus(
          selectedCenter,
          { x: containerWidth / 2, y: containerHeight / 2 },
          clampedScale,
          rect,
          { width: pageWidth, height: pHeight },
        );
        setScale(clampedScale);
        setTranslateX(focused.translateX);
        setTranslateY(focused.translateY);
        return;
      }

      if (options?.anchorPoint) {
        const prevScale = scaleRef.current;
        const currentElementX = (options.anchorPoint.x - translateXRef.current) / prevScale;
        const currentElementY = (options.anchorPoint.y - translateYRef.current) / prevScale;
        let newTranslateX = options.anchorPoint.x - currentElementX * clampedScale;
        let newTranslateY = options.anchorPoint.y - currentElementY * clampedScale;
        const scaledWidth = pageWidth * clampedScale;
        const scaledHeight = pHeight * clampedScale;

        if (scaledWidth <= rect.width) {
          newTranslateX = (rect.width - scaledWidth) / 2;
        }
        if (scaledHeight <= rect.height) {
          newTranslateY = (rect.height - scaledHeight) / 2;
        }

        setScale(clampedScale);
        setTranslateX(newTranslateX);
        setTranslateY(newTranslateY);
        return;
      }

      const {
        scale: newScale,
        translateX,
        translateY,
      } = getNewFinalTranslation(
        clampedScale,
        { width: pageWidth, height: pHeight },
        { width: containerWidth, height: containerHeight },
      );
      setScale(newScale);
      setTranslateX(translateX);
      setTranslateY(translateY);
    },
    [
      clampScale,
      frameRef,
      getSelectedElementCenter,
      getTranslateForFocus,
      getUnscaledPageHeight,
      pageWidth,
    ],
  );

  /** Pan only (no zoom): move the page by how much the element is off-screen so it comes into view. */
  const scrollCanvasToNode = useCallback(
    (nodeId: string) => {
      if (!frameRef || !pageDom || nodeId === ROOT_NODE) return;
      const ancestors = query.node(nodeId).ancestors(true);
      ancestors.pop(); //remove root node
      let targetNodeId = nodeId;
      if (ancestors.length > 0) {
        targetNodeId = ancestors[ancestors.length - 1]; //get the root container ancestor node id
      }

      const node = query.node(targetNodeId).get();
      const nodeDom = node?.dom as HTMLElement | undefined;
      if (!nodeDom) return;

      const viewportRect = frameRef.getBoundingClientRect();
      const nodeRect = nodeDom.getBoundingClientRect();

      const elementRectInViewport = {
        left: nodeRect.left - viewportRect.left,
        top: nodeRect.top - viewportRect.top,
        right: nodeRect.right - viewportRect.left,
        bottom: nodeRect.bottom - viewportRect.top,
      };

      const result = getTranslateToScrollIntoView({
        viewportWidth: viewportRect.width,
        viewportHeight: viewportRect.height,
        translateX: translateXRef.current,
        translateY: translateYRef.current,
        elementRectInViewport,
      });

      if (result) {
        setTranslateX(result.translateX);
        setTranslateY(result.translateY);
      }
    },
    [frameRef, pageDom, query],
  );

  useEffect(() => {
    registerFocusCanvas(scrollCanvasToNode);
    return () => registerFocusCanvas(null);
  }, [registerFocusCanvas, scrollCanvasToNode]);

  useEffect(() => {
    const wrapperDiv = frameRef;
    if (!wrapperDiv) return;
    const handleMouseEnter = () => setIsCanvasActive(true);
    const handleMouseLeave = () => setIsCanvasActive(false);
    wrapperDiv.addEventListener('mouseenter', handleMouseEnter);
    wrapperDiv.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      wrapperDiv.removeEventListener('mouseenter', handleMouseEnter);
      wrapperDiv.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [frameRef]);

  // Zoom to fit on mount and when page dimensions change (e.g. breakpoint). Window resize is ignored.
  useEffect(() => {
    const wrapperDiv = frameRef;
    if (!wrapperDiv) return;

    if (pageWidth === undefined) {
      // Fullscreen: canvas fills 100% viewport, no scaling needed
      setScale(1);
      setTranslateX(0);
      setTranslateY(0);
      return;
    }

    const rect = wrapperDiv.getBoundingClientRect();
    const wrapperDimension = { width: rect.width, height: rect.height };
    const pageDimension = { width: pageWidth, height: pageHeight };
    const res = zoomPageToFitScreen(pageDimension, wrapperDimension);
    setScale(res.scale);
    setTranslateX(res.translateX);
    setTranslateY(res.translateY);
  }, [pageWidth, pageHeight, frameRef]);

  // Prevent browser zoom (Ctrl/Cmd+scroll) so only the builder canvas zoom is used
  useEffect(() => {
    const preventBrowserZoom = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
      }
    };
    document.addEventListener('wheel', preventBrowserZoom, { passive: false, capture: true });
    return () => {
      document.removeEventListener('wheel', preventBrowserZoom, { capture: true });
    };
  }, []);

  // Wheel zoom and pan
  useEffect(() => {
    if (!frameRef) return;
    const SCROLL_SPEED = 0.9;
    const ZOOM_SPEED = 0.0005;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      dispatch(setIsPanningMode(true));
      if (wheelTimeout.current) clearTimeout(wheelTimeout.current);
      wheelTimeout.current = setTimeout(() => {
        dispatch(setIsPanningMode(false));
      }, 200);

      if (e.ctrlKey || e.metaKey) {
        const el = frameRef;
        if (!el) return;
        const rect = el.getBoundingClientRect();

        // Get mouse position relative to the canvas container
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        const nextScale = scaleRef.current - e.deltaY * ZOOM_SPEED;
        applyZoomScale(nextScale, {
          focusSelected: true,
          anchorPoint: { x: mouseX, y: mouseY },
        });
      } else if (e.altKey) {
        setTranslateX((prev) => {
          const newTranslateX = prev - e.deltaY * SCROLL_SPEED;
          return newTranslateX;
        });
      } else {
        setTranslateY((prev) => {
          const newTranslateY = prev - e.deltaY * SCROLL_SPEED;
          return newTranslateY;
        });
      }
    };
    frameRef.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      if (frameRef) frameRef.removeEventListener('wheel', handleWheel);
      if (wheelTimeout.current) clearTimeout(wheelTimeout.current);
    };
  }, [applyZoomScale, frameRef]);

  // Horizontal wheel scroll (e.g., MX Master thumb wheel)
  useEffect(() => {
    const el = frameRef;
    if (!el) return;
    const handleWheelHorizontal = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) return; // zoom handled elsewhere
      const horizontalDelta = e.deltaX; // native horizontal only
      if (!horizontalDelta) return;
      e.preventDefault();
      const SPEED = 1;
      setTranslateX((prev) => prev - horizontalDelta * SPEED);
    };
    el.addEventListener('wheel', handleWheelHorizontal, { passive: false });
    return () => {
      el.removeEventListener('wheel', handleWheelHorizontal);
    };
  }, [frameRef]);

  // Panning with spacebar and mouse
  useEffect(() => {
    const frame = frameRef;
    if (!frame) return;
    let rafId: number | null = null;
    let lastDx = 0;
    let lastDy = 0;
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const contentEditable = target.getAttribute('contenteditable');
      if (
        e.code === 'Space' &&
        target instanceof HTMLElement &&
        target.tagName !== 'INPUT' &&
        target.tagName !== 'TEXTAREA' &&
        contentEditable !== 'true'
      ) {
        e.preventDefault();
        setPanningModeEnabled(true);
        dispatch(setIsPanningMode(true));
        document.body.style.cursor = 'grab';
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setPanningModeEnabled(false);
        dispatch(setIsPanningMode(false));
        document.body.style.cursor = 'default';
      }
    };
    const handleMouseDown = (e: MouseEvent) => {
      if (!panningModeEnabled || e.button !== 0) return;
      e.preventDefault();
      isDragging.current = true;
      startX.current = e.clientX;
      startY.current = e.clientY;
      originX.current = translateXRef.current;
      originY.current = translateYRef.current;
      document.body.style.cursor = 'grabbing';
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    };
    const updateTranslate = () => {
      setTranslateX(originX.current + lastDx);
      setTranslateY(originY.current + lastDy);
      rafId = null;
    };
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      document.body.style.cursor = 'grabbing';
      lastDx = e.clientX - startX.current;
      lastDy = e.clientY - startY.current;
      if (rafId === null) {
        rafId = window.requestAnimationFrame(updateTranslate);
      }
    };
    const handleMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = panningModeEnabled ? 'grab' : 'default';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    frame.addEventListener('mousedown', handleMouseDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      frame.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [panningModeEnabled, dispatch, frameRef]);

  useEffect(() => {
    dispatch(setPanZoom({ scale, translateX, translateY }));
  }, [scale, translateX, translateY, dispatch]);

  // Sync Redux translate values back to local state when changed externally
  // (e.g., auto-scroll from PopupClickTriggerPanel)
  useEffect(() => {
    setTranslateX(reduxTranslateX);
    setTranslateY(reduxTranslateY);
  }, [reduxTranslateX, reduxTranslateY]);

  const optionRefs = useRef<Record<string, HTMLButtonElement | null>>({
    desktop: null,
    tablet: null,
    mobile: null,
  });

  useEffect(() => {
    const currentBreakpoint = storeBreakpoint;
    if (currentBreakpoint !== selectedSize) {
      setSelectedSize(currentBreakpoint);
    }
  }, [storeBreakpoint, selectedSize]);

  useLayoutEffect(() => {
    const activeEl = optionRefs.current[selectedSize];
    if (activeEl) {
      const parent = (activeEl.offsetParent || activeEl.parentElement) as HTMLElement | null;
      const activeRect = activeEl.getBoundingClientRect();
      const parentRect = parent?.getBoundingClientRect();
      const left = parentRect
        ? Math.round(activeRect.left - parentRect.left + (parent?.scrollLeft || 0))
        : Math.round(activeEl.offsetLeft);
      setHighlightStyle({
        width: Math.round(activeRect.width),
        left,
      });
    }
  }, [selectedSize]);

  const handleBreakpointClick = (newBreakpoint: Breakpoint) => {
    if (newBreakpoint !== selectedSize) {
      track(EVENT.DEVICE_VIEW_CHANGED, {
        [PROPERTY.VIEW_TYPE]: newBreakpoint,
      });
      switchBreakpoint(newBreakpoint);
      setIsAnimated(true);
      setSelectedSize(newBreakpoint);
    }
  };

  const applyZoomAction = useCallback(
    (zoomValue: string, focusSelected = false) => {
      const currentScale = scaleRef.current;

      switch (zoomValue) {
        case 'Zoom Out': {
          applyZoomScale(currentScale - 0.1, { focusSelected });
          break;
        }
        case 'Zoom In': {
          applyZoomScale(currentScale + 0.1, { focusSelected });
          break;
        }

        case '25%': {
          applyZoomScale(0.25, { focusSelected });
          break;
        }
        case '50%': {
          applyZoomScale(0.5, { focusSelected });
          break;
        }
        case '100%': {
          applyZoomScale(1, { focusSelected });
          break;
        }
        case '200%': {
          applyZoomScale(2, { focusSelected });
          break;
        }
        case 'Zoom to Fit': {
          if (pageWidth === undefined) return;
          const canvasContainer =
            frameRef || (document.querySelector('.page-container') as HTMLElement | null);
          if (!canvasContainer) return;
          const rect = canvasContainer.getBoundingClientRect();
          const unscaledHeight = getUnscaledPageHeight();
          if (rect.width <= 0 || rect.height <= 0) return;
          const pageH = unscaledHeight > 0 ? unscaledHeight : pageHeight;
          const {
            scale: newScale,
            translateX: newTranslateX,
            translateY: newTranslateY,
          } = zoomPageToFitScreen(
            {
              width: pageWidth,
              height: pageH,
            },
            {
              width: rect.width,
              height: rect.height,
            },
          );
          setScale(newScale);
          setTranslateX(newTranslateX);
          setTranslateY(newTranslateY);
          break;
        }

        default:
          break;
      }
    },
    [applyZoomScale, frameRef, getUnscaledPageHeight, pageWidth, pageHeight],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return;
      }

      if (!isCanvasActive && !isZoomPanelActive) return;
      if (!e.metaKey && !e.ctrlKey) return;

      const key = e.key;
      if (key === '+' || key === '=') {
        e.preventDefault();
        applyZoomAction('Zoom In', true);
        return;
      }
      if (key === '-' || key === '_') {
        e.preventDefault();
        applyZoomAction('Zoom Out', true);
        return;
      }
      if (key === '0') {
        e.preventDefault();
        applyZoomAction('Zoom to Fit');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [applyZoomAction, isCanvasActive, isZoomPanelActive]);

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const isLoadingState = !!loading;

  if (isLoadingState) {
    return null;
  }

  return (
    <div
      className={clsx(
        'responsive-panel bg-neutral-0 inline-flex items-center justify-start gap-2 rounded-xl px-2 py-2 shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] outline outline-offset-[-1px] outline-neutral-100',
        className,
      )}
      onMouseEnter={() => setIsZoomPanelActive(true)}
      onMouseLeave={() => setIsZoomPanelActive(false)}
    >
      <div className="relative flex h-8 items-center overflow-hidden rounded-md bg-neutral-50 shadow-[inset_0px_0px_2px_0px_rgba(0,0,0,0.12)]">
        <div
          className={`absolute top-0 z-10 flex h-full items-center justify-center rounded-md bg-white text-indigo-500 outline outline-offset-[-1px] outline-neutral-100 dark:bg-white ${
            isAnimated ? 'transition-all duration-300' : ''
          }`}
          style={{
            width: `${highlightStyle.width}px`,
            left: `${highlightStyle.left}px`,
          }}
        ></div>

        {breakpoints.map((breakpoint) => {
          const isActive = selectedSize === breakpoint;
          const Icon = icons[breakpoint].inactive;
          const ActiveIcon = icons[breakpoint].active;

          return (
            <button
              key={breakpoint}
              ref={(el) => {
                optionRefs.current[breakpoint] = el;
              }}
              onClick={() => handleBreakpointClick(breakpoint)}
              data-active={isActive}
              data-testid={`breakpoint-toggle-${breakpoint}`}
              data-qa-selector={`toolbar-breakpoint-toggle-${breakpoint}`}
              className={`group text-button-sm relative z-10 cursor-pointer p-[7px] capitalize ${
                isActive ? 'text-indigo-500' : 'text-neutral-400'
              } hover:text-indigo-500`}
            >
              <span className="relative flex items-center justify-center">
                {isActive ? (
                  <ActiveIcon className="h-4.5 w-4.5 text-indigo-500" />
                ) : (
                  <>
                    <span className="absolute opacity-0 transition-opacity group-hover:opacity-100">
                      <ActiveIcon className="h-4.5 w-4.5 text-indigo-500" />
                    </span>
                    <span className="transition-opacity group-hover:opacity-0">
                      <Icon className="h-4.5 w-4.5 text-neutral-400 group-hover:text-indigo-500" />
                    </span>
                  </>
                )}
              </span>
            </button>
          );
        })}
      </div>

      <ZoomSelect
        className="responsive-preview group w-[68px]"
        classNameContent="min-w-[160px]"
        value={Math.round(scale * 100) + '%'}
        customValues={true}
        size="none"
        text=""
        triggerRef={triggerRef}
        contentSide="top"
        onValueChange={(v) => applyZoomAction(v, true)}
        data-qa-selector="toolbar-zoom-select"
      >
        <>
          <SelectItem className="group w-full" value="Zoom In">
            <div className="flex w-full max-w-full items-center justify-between gap-2">
              <span className="text-button-sm text-neutral-500 group-data-[state=checked]:text-indigo-500 group-data-[state=checked]:hover:!text-indigo-500">
                Zoom In
              </span>{' '}
              <span className="text-body-xs text-normal text-neutral-400 group-data-[state=checked]:text-indigo-500 group-data-[state=checked]:hover:!text-indigo-500">
                ⌘+
              </span>
            </div>
          </SelectItem>
          <SelectItem className="group w-full" value="Zoom Out">
            <div className="flex w-full max-w-full items-center justify-between gap-2">
              <span className="text-button-sm text-neutral-500 group-data-[state=checked]:text-indigo-500 group-data-[state=checked]:hover:!text-indigo-500">
                Zoom Out
              </span>{' '}
              <span className="text-body-xs text-normal text-neutral-400 group-data-[state=checked]:text-indigo-500 group-data-[state=checked]:hover:!text-indigo-500">
                ⌘-
              </span>
            </div>
          </SelectItem>
          <SelectItem className="group w-full" value="Zoom to Fit">
            <div className="flex w-full max-w-full items-center justify-between gap-2">
              <span className="text-button-sm text-neutral-500 group-data-[state=checked]:text-indigo-500 group-data-[state=checked]:hover:!text-indigo-500">
                Zoom to Fit
              </span>{' '}
              <span className="text-body-xs text-normal text-neutral-400 group-data-[state=checked]:text-indigo-500 group-data-[state=checked]:hover:!text-indigo-500">
                ⌘+0
              </span>
            </div>
          </SelectItem>
          <div className="my-1 h-[1px] w-full bg-neutral-100"></div>
          <SelectItem value="25%">Zoom to 25%</SelectItem>
          <SelectItem value="50%">Zoom to 50%</SelectItem>
          <SelectItem value="100%">Zoom to 100%</SelectItem>
          <SelectItem value="200%">Zoom to 200%</SelectItem>
        </>
      </ZoomSelect>

      <Tooltip textFirst="Recenter" textSecond="" className="" position="top">
        <button
          type="button"
          aria-label="Recenter canvas"
          onClick={() => applyZoomAction('Zoom to Fit')}
          data-testid="toolbar-recenter"
          data-qa-selector="toolbar-recenter"
          className="group cursor-pointer rounded-md p-1 outline-1 outline-offset-[-0.88px] outline-neutral-100 hover:bg-neutral-100 active:bg-neutral-100"
        >
          <span className="relative flex h-4 w-4 items-center justify-center">
            <span className="absolute opacity-100 transition-opacity group-hover:opacity-0 group-active:opacity-0">
              <IconRecenter className="h-4 w-4" />
            </span>
            <span className="absolute opacity-0 transition-opacity group-hover:opacity-100 group-active:opacity-100">
              <IconRecenterFocus className="h-4 w-4" />
            </span>
          </span>
        </button>
      </Tooltip>

      <LightDarkMode className="" data-qa-selector="toolbar-light-dark-mode-toggle" />
    </div>
  );
};
